import { Fragment, ReactNode, useMemo, useState } from 'react'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ListItemButtonWithNavigation from '../input/ListItemButtonWithNavigation'
import { matchPath, useLocation } from 'react-router-dom'

export type DrawerItem = {
  icon?: ReactNode
  text: string
  key: string
  path: string
}

export type DrawerSection = {
  title: string
  id?: string
  items: readonly DrawerItem[]
  dividerBefore?: boolean
}

export type DrawerScaffoldProps = {
  sections: readonly DrawerSection[]
  children: ReactNode
}

const getSectionKey = (section: DrawerSection, index: number) =>
  section.id ?? `${section.title.toLowerCase().replace(/\s+/g, '-')}-${index}`

const DrawerScaffold = ({ sections, children }: DrawerScaffoldProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const location = useLocation()

  const isPathActive = (path: string) =>
    !!matchPath(
      {
        path,
        end: false,
      },
      location.pathname
    )

  const grouped = useMemo(() => {
    const visibleSections = sections.filter((section) => section.items.length > 0)
    return visibleSections.map((section, index) => ({
      ...section,
      key: getSectionKey(section, index),
      dividerBefore:
        section.dividerBefore ??
        (index > 0 && visibleSections.slice(0, index).some((prevSection) => prevSection.items.length > 0)),
    }))
  }, [sections])

  const toggle = (key: string, expandedState: boolean) =>
    setExpanded((prev) => ({
      ...prev,
      [key]: expandedState,
    }))

  return (
    <Box className="drawerScaffold">
      <Drawer variant="permanent" PaperProps={{ elevation: 2 }} className="drawer">
        <Toolbar />
        {grouped.map(({ title, items, dividerBefore, key }) => {
          const sectionHasActiveItem = items.some(({ path }) => isPathActive(path))
          const isExpanded = expanded[key] ?? sectionHasActiveItem
          return (
            <Fragment key={key}>
              {dividerBefore && <Divider />}
              <Accordion
                expanded={isExpanded}
                onChange={(event, expandedState) => toggle(key, expandedState)}
                disableGutters
                square
                elevation={0}
                sx={{
                  bgcolor: 'transparent',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  aria-controls={`${key}-content`}
                  id={`${key}-header`}
                  sx={{
                    px: 2,
                    py: 0,
                    minHeight: 18,
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                      alignItems: 'center',
                    },
                  }}
                >
                  <ListItemText
                    primary={title}
                    primaryTypographyProps={{
                      variant: 'overline',
                      letterSpacing: 1.5,
                      fontSize: '0.7rem',
                      color: sectionHasActiveItem ? 'primary' : undefined,
                    }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {items.map(({ icon, text, key: itemKey, path }) => (
                      <ListItem key={`item-${itemKey}`} disablePadding>
                        <ListItemButtonWithNavigation path={path}>
                          {icon && <ListItemIcon sx={{ minWidth: 30 }}>{icon}</ListItemIcon>}
                          <ListItemText primary={text} primaryTypographyProps={{ fontSize: '0.82rem' }} />
                        </ListItemButtonWithNavigation>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Fragment>
          )
        })}
      </Drawer>
      <Stack sx={{ flexGrow: 1, overflowX: 'hidden' }}>{children}</Stack>
    </Box>
  )
}

export default DrawerScaffold
